import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Repeat } from "lucide-react";
import { HISTORY_WINDOW_LABELS, type FirstPlan, type ProfileDetail } from "@amber/profiles/contracts";
import { profileCommands } from "@amber/profiles/ui";
import {
  Button,
  Drawer,
  DrawerSection,
  FailedCallout,
  ReasonField,
  Tag,
  useReason,
  useToast,
  useCommandMutation,
} from "@amber/ui";
import { historyQuery, AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT } from "../queries";
import { Estimate } from "./Estimate";
import { PlatformCode } from "./PlatformCode";

function surname(name: string): string {
  return name.split(" ").at(-1) ?? name;
}

function CoveredSection({ covered, name }: { covered: FirstPlan["covered"]; name: string }) {
  return (
    <DrawerSection title="Already covered">
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {covered.map((c) => (
          <li key={c.source_id} className="flex items-start gap-2 rounded-lg border border-divider px-2.5 py-2">
            <Repeat size={13} aria-hidden="true" className="mt-0.5 text-muted" />
            <span className="min-w-0 flex-1">
              <b className="font-strong text-fg">{c.title}</b> <span className="text-muted">· {c.why}</span>
              <span className="block text-md text-muted">
                Already scanned through {c.monitored_for.join(", ")}; no extra scanning or cost. Appearances there count
                for {name}.
              </span>
            </span>
          </li>
        ))}
      </ul>
    </DrawerSection>
  );
}

function HistorySection({ plan, name }: { plan: FirstPlan; name: string }) {
  const central = useQuery(historyQuery).data;
  return (
    <DrawerSection title="History and recheck">
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        <li className="flex items-start gap-2 rounded-lg border border-divider px-2.5 py-2">
          <Plus size={13} aria-hidden="true" className="mt-0.5 text-link" />
          <span>
            <b className="font-strong">History: Default</b>
            <span className="block text-md text-muted">
              Follows the central window: {HISTORY_WINDOW_LABELS[central?.window ?? "2_weeks"]} before today. Change it
              later with Edit plan.
            </span>
          </span>
        </li>
        <li className="flex items-start gap-2 rounded-lg border border-divider px-2.5 py-2">
          <Plus size={13} aria-hidden="true" className="mt-0.5 text-link" />
          <span>
            <b className="font-strong">
              About {plan.recheck_estimate.toLocaleString("en")} archived item
              {plan.recheck_estimate === 1 ? " is" : "s are"} rechecked for {name}
            </b>
            <span className="block text-md text-muted">
              Items already archived or discovered from other plans and shared sources within the history window,
              including items excluded as not relevant. Items where {name} speaks are processed like newly discovered
              items; progress shows on the profile.
            </span>
          </span>
        </li>
      </ul>
    </DrawerSection>
  );
}

function ApprovedList({
  approved,
  sources,
  onChange,
}: {
  approved: FirstPlan["approved"];
  sources: readonly string[];
  onChange: (sources: string[]) => void;
}) {
  if (approved.length === 0) return null;
  return (
    <ul className="m-0 mb-1.5 flex list-none flex-col gap-1.5 p-0" aria-label="Approved sources">
      {approved.map((a) => (
        <li key={a.source_id}>
          <label className="flex items-start gap-2 rounded-lg border border-divider px-2.5 py-2">
            <input
              type="checkbox"
              className="mt-0.5 accent-[var(--color-link)]"
              checked={sources.includes(a.source_id)}
              onChange={(e) => {
                onChange(e.target.checked ? [...sources, a.source_id] : sources.filter((x) => x !== a.source_id));
              }}
            />
            <PlatformCode code={a.platform_code} />
            <span className="min-w-0 flex-1">
              <b className="block truncate font-strong text-fg">{a.title}</b>
              <span className="block text-md text-muted">Approved · not in any plan yet</span>
            </span>
            {a.relationship === "official_record" ? <Tag tone="strong">Required</Tag> : null}
          </label>
        </li>
      ))}
    </ul>
  );
}

export function StartTracking({
  profile,
  plan,
  onClose,
}: {
  profile: ProfileDetail;
  plan: FirstPlan;
  onClose: () => void;
}) {
  const toast = useToast();
  const [selected, setSelected] = useState<string[]>(plan.preselected.map((s) => s.id));
  const [sources, setSources] = useState<string[]>(plan.approved.map((a) => a.source_id));
  const reason = useReason();
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      profileCommands.startTracking(
        profile.id,
        {
          selected,
          sources,
          history: { option: "default", since: null },
          reason: text,
        },
        csrf,
      ),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: () => {
      toast.show("Tracking started · waiting for the scheduler");
      onClose();
    },
  });
  const chosen = plan.preselected.filter((s) => selected.includes(s.id));
  const chosenApproved = plan.approved.filter((a) => sources.includes(a.source_id));
  const nothing = chosen.length === 0 && chosenApproved.length === 0 && plan.covered.length === 0;
  const stayOpen = plan.suggestions.length + (plan.preselected.length - chosen.length);
  const name = surname(profile.name);
  return (
    <Drawer
      title="Start tracking"
      subtitle={profile.name}
      onClose={onClose}
      footer={
        <span className="flex items-center justify-end gap-2">
          <span className="mr-auto text-md text-muted">Nothing changes until you confirm.</span>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            form="start-tracking-form"
            disabledReason={nothing ? "Select at least one source to monitor" : null}
            aria-busy={mutation.isPending || undefined}
          >
            Start tracking
          </Button>
        </span>
      }
    >
      <form
        id="start-tracking-form"
        noValidate
        className="flex flex-col gap-4 text-ui"
        onSubmit={(e) => {
          e.preventDefault();
          if (!reason.submit() || nothing) return;
          mutation.mutate(reason.trimmed);
        }}
      >
        <DrawerSection title="Own sources to approve and monitor">
          {plan.preselected.length === 0 && plan.approved.length === 0 ? (
            <p className="m-0 text-md text-muted">
              No own source with strong evidence was found. Check the suggestions on the profile.
            </p>
          ) : null}
          <ApprovedList approved={plan.approved} sources={sources} onChange={setSources} />
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {plan.preselected.map((s) => (
              <li key={s.id}>
                <label className="flex items-start gap-2 rounded-lg border border-divider px-2.5 py-2">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-[var(--color-link)]"
                    checked={selected.includes(s.id)}
                    onChange={(e) => {
                      setSelected(e.target.checked ? [...selected, s.id] : selected.filter((x) => x !== s.id));
                    }}
                  />
                  <PlatformCode code={s.platform_code} />
                  <span className="min-w-0 flex-1">
                    <b className="block truncate font-strong text-fg">{s.title}</b>
                    <span className="block text-md text-muted">{s.why}</span>
                  </span>
                  {s.relationship === "official_record" ? <Tag tone="strong">Required</Tag> : null}
                </label>
              </li>
            ))}
          </ul>
          {plan.preselected.length > chosen.length ? (
            <p className="m-0 mt-1.5 text-md text-muted">A deselected source stays open as a suggestion.</p>
          ) : null}
        </DrawerSection>
        {plan.covered.length > 0 ? <CoveredSection covered={plan.covered} name={name} /> : null}
        <DrawerSection title="Estimate">
          <Estimate plan={plan} selected={[...chosen, ...chosenApproved]} />
        </DrawerSection>
        <HistorySection plan={plan} name={name} />
        <DrawerSection title="Stays open">
          <p className="m-0 text-md">
            {stayOpen} suggestion{stayOpen === 1 ? " stays" : "s stay"} open for individual decisions. Languages:{" "}
            {profile.languages.map((l) => l.toUpperCase()).join(", ") || "none"}. Owner: {profile.owner ?? "not set"}.
          </p>
        </DrawerSection>
        <ReasonField
          value={reason.reason}
          onChange={reason.setReason}
          error={reason.error}
          placeholder="e.g. Newly appointed minister; speaks often on budget policy"
        />
        <FailedCallout error={mutation.error} className="" />
      </form>
    </Drawer>
  );
}
